/**
 * The Value Dashboard's one-click cell: its own bordered toolbar cell, so the
 * armed state reads as a page-level mode, not a filter chip.
 *
 * H-1 — every part is a fixed slot inside the cell's height: 1-Click and
 * Attack, the hotkey readout's fixed frame, the duration badge, and the status
 * box. Arming, aiming, an ack, its legs or a disarm reason only change text
 * inside boxes that are always there, so no control slides under the pointer
 * and the board below never moves. (Side by side, the same slots made a 769px
 * cell that wrapped the toolbar at 1470 and pushed the board down.)
 *
 * #1125 — below 2200px the cell spends height, not width: side by side, the
 * arm row alone was 504px and the readout row 336px, and the filter toolbar
 * beside the cell wrapped into a taller header; stacked, the cell is about as
 * tall as the toolbar anyway. From 2200px the toolbar has room to spare (it
 * keeps at least the width it has stacked at 1920), and the stacked cell would
 * be the taller of the two, so the slots go side by side again. The status box
 * is six lines (H-2 was 31rem by two, which hid a refused S leg): a refused S
 * leg and its reason fit in full at 18rem.
 *
 * Nothing that can go away mid-session may move what stays. Attack's row
 * keeps its size when Attack is turned off on its own (its notice lands in
 * the status box right then): its width as well as its height, since size
 * boxes are as wide as the trader's ceiling and no fixed width covers every
 * arm row, and Attack is the same size in either layout. When one-click
 * access goes (D5: the cell stays with only the status line, which names the
 * revocation, until the trader dismisses it), the stand-in takes the control
 * block's last size.
 */
import { useCallback, useRef, useState, type CSSProperties } from 'react';
import { X } from 'lucide-react';
import { Lt2HotkeyListener } from '@/components/Lt2HotkeyListener';
import { useAttackAccess } from '@/hooks/useAttackAccess';
import { cn } from '@/lib/infra/utils';
import { Lt2TargetReadoutFrame, Lt2TargetReadoutSlot } from '@/components/Lt2TargetReadout';
import { AttackArmControl } from './AttackArmControl';
import { OneClickArmControl, OneClickStatusSlot } from './OneClickArmControl';
import { OneClickDurationBadge } from './OneClickDurationBadge';
import type { OneClickRevocation } from './useOneClickRevocation';

const DASHBOARD_DURATION = { kind: 'dashboard' } as const;

/**
 * The size an element last had while it was mounted (undefined before it ever
 * was). It keeps that value after the element goes, which is what lets a
 * stand-in take the element's place at the same size. An element that holds
 * its own last size as its minimum never measures smaller, so it never
 * shrinks.
 */
type Size = { width: number; height: number };
type HeldSize = Size & { stacked?: Size; wide?: Size };
const measuredSize = (el: HTMLElement): Size => {
  const rect = el.getBoundingClientRect();
  return { width: rect.width || el.offsetWidth, height: rect.height || el.offsetHeight };
};

// Capture both CSS layouts from their fixed children while controls exist.
// A revoked block has no observer: retaining just its last measured layout
// would hold the wide width after the window narrows (and the tall height
// after it widens). This runs on layout changes, never on quote updates.
function controlSizes(el: HTMLElement): Pick<HeldSize, 'stacked' | 'wide'> {
  const groups = Array.from(el.children).map(group => Array.from(group.children)
    .filter((child): child is HTMLElement => child instanceof HTMLElement && child.offsetWidth > 0 && child.offsetHeight > 0)
    .map(measuredSize));
  if (!groups.length || groups.some(group => !group.length)) return {};
  const size = (wide: boolean): Size => {
    const rows = groups.map(group => wide
      ? { width: group.reduce((n, child) => n + child.width, 0) + 8 * (group.length - 1), height: Math.max(...group.map(child => child.height)) }
      : { width: Math.max(...group.map(child => child.width)), height: group.reduce((n, child) => n + child.height, 0) + 4 * (group.length - 1) });
    return { width: Math.max(...rows.map(row => row.width)), height: rows.reduce((n, row) => n + row.height, 0) + 4 * (rows.length - 1) };
  };
  return { stacked: size(false), wide: size(true) };
}

function useLastSize(responsiveControls = false): [(el: HTMLElement | null) => void, HeldSize | undefined] {
  const [size, setSize] = useState<HeldSize>();
  const observer = useRef<ResizeObserver | null>(null);
  const ref = useCallback((el: HTMLElement | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!el) return;
    const measure = () => {
      const next: HeldSize = { ...measuredSize(el),
        ...(responsiveControls ? controlSizes(el) : {}) };
      setSize(prev => JSON.stringify(prev) === JSON.stringify(next) ? prev : next);
    };
    measure();
    observer.current = new ResizeObserver(measure);
    observer.current.observe(el);
  }, [responsiveControls]);
  return [ref, size];
}

export function OneClickToolbarCell({
  listener,
  revocation,
}: {
  /** Mount the A/D hotkey listener (lt2 access and the hotkey setting on). */
  listener: boolean;
  revocation: OneClickRevocation;
}) {
  const { revoked, holdFrame, dismiss } = revocation;
  return (
    <div
      data-testid="one-click-toolbar-cell"
      className="border-border flex shrink-0 flex-col justify-center gap-1 border-l px-3"
    >
      <OneClickControls revoked={revoked} holdFrame={holdFrame} dismiss={dismiss} />
      <div className="min-w-[18rem]">
        <OneClickStatusSlot lines={6} />
      </div>
      {/* D-020 V2B: A/D hotkey listener — dark unless the trader has
          lt2Access AND flipped the hotkey setting on. */}
      {!revoked && listener && <Lt2HotkeyListener />}
    </div>
  );
}

/** Everything above the status box, or its D5 stand-in. */
function OneClickControls({ revoked, holdFrame, dismiss }: Pick<OneClickRevocation, 'revoked' | 'holdFrame' | 'dismiss'>) {
  const [controlsRef, controls] = useLastSize(true);
  const [attackRowRef, attackRow] = useLastSize();
  // Attack's row is held only once this session has confirmed the grant: a
  // restored grant that comes back "off" leaves no empty row behind.
  const attackConfirmed = useAttackAccess();
  const [attackWasConfirmed, setAttackWasConfirmed] = useState(attackConfirmed);
  if (attackConfirmed && !attackWasConfirmed) setAttackWasConfirmed(true);
  const attackRowSize = attackWasConfirmed && attackRow?.height ? attackRow : undefined;
  if (revoked) {
    // Dismiss (bordered like the arm buttons) in 1-Click's row, Attack's held
    // row, and the readout's empty frame stand in for the control block, at
    // its size, so the frame stays where the readout was.
    return (
      <div
        data-testid="one-click-controls-held"
        className="flex flex-col items-start gap-1 self-start min-w-[var(--held-stacked-w)] min-h-[var(--held-stacked-h)] min-[2200px]:min-w-[var(--held-wide-w)] min-[2200px]:min-h-[var(--held-wide-h)]"
        style={{
          '--held-stacked-w': `${controls?.stacked?.width ?? controls?.width ?? 0}px`,
          '--held-stacked-h': `${controls?.stacked?.height ?? controls?.height ?? 0}px`,
          '--held-wide-w': `${controls?.wide?.width ?? controls?.width ?? 0}px`,
          '--held-wide-h': `${controls?.wide?.height ?? controls?.height ?? 0}px`,
        } as CSSProperties}
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss the one-click notice"
          className="text-muted-foreground hover:text-foreground flex items-center gap-1 rounded border border-transparent px-1.5 py-1 text-xs"
        >
          <X size={13} aria-hidden />
          Dismiss
        </button>
        {/* Attack's own row exists only while stacked. */}
        {attackRowSize ? (
          <div data-testid="one-click-attack-row-held" aria-hidden className="min-[2200px]:hidden" style={{ height: attackRowSize.height }} />
        ) : null}
        {holdFrame && <Lt2TargetReadoutFrame />}
      </div>
    );
  }
  return (
    <div
      ref={controlsRef}
      data-testid="one-click-controls"
      className="flex flex-col items-start gap-1 self-start"
    >
      <div
        data-testid="one-click-arm-rows"
        className="flex flex-col items-start gap-1 min-[2200px]:flex-row min-[2200px]:items-center min-[2200px]:gap-2"
      >
        <OneClickArmControl />
        {/* V3: the aggressive sibling — hides without the attack grant,
            disabled until one-click arms. A trader who never had it gets no
            row (and no gap); one who had it keeps the row's size. */}
        <div
          data-testid="one-click-attack-row"
          ref={attackRowRef}
          className={cn(!attackRowSize && 'empty:hidden')}
          style={{ minWidth: attackRowSize?.width, minHeight: attackRowSize?.height }}
        >
          <AttackArmControl />
        </div>
      </div>
      {/* Global A/D target readout — where the hotkeys would land, from the
          same focus store the keypress fires through. A fixed frame stands in
          while disarmed, so arming fills a box already there. Good to Start:
          under it (as in Game Detail) or beside it, the duration a press uses
          (the Settings default here), in its own fixed slot. */}
      <div className="flex flex-col items-start gap-1 min-[2200px]:flex-row min-[2200px]:gap-2">
        <Lt2TargetReadoutSlot />
        <OneClickDurationBadge scope={DASHBOARD_DURATION} />
      </div>
    </div>
  );
}
